/**
 * Adventure's anatomical pedestrian, exported from the CC0 MakeHuman base,
 * fitted clothes and skin. See public/models/neighborhood/benchmark/README.md.
 * The simulation owns position/yaw/speed; this adapter only poses the skin.
 */
import type { Scene } from '@babylonjs/core/scene';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import type { AssetContainer } from '@babylonjs/core/assetContainer';
import type { AnimationGroup } from '@babylonjs/core/Animations/animationGroup';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { buildAdventurePedestrian as buildFallbackPedestrian, type AdventureActor } from './adventureCast';
import { themeAssetPath } from './themes';
import '@babylonjs/loaders/glTF';

type LoadState = { pending: Set<string>; failed: Set<string> };
export interface AdventurePedestrianOptions {
  palette?: number;
  carryingBag?: boolean;
  loadState?: LoadState;
  onReady?: (meshes: Mesh[]) => void;
}
type RestPose = { node: TransformNode; position: Vector3; rotation: Quaternion };

/** Creates a stable actor immediately; embedded GLB geometry arrives asynchronously. */
export function buildAdventurePedestrian(scene: Scene, options: AdventurePedestrianOptions = {}): AdventureActor {
  const root = new TransformNode('adventurePedestrian', scene);
  const meshes: Mesh[] = [];
  const path = themeAssetPath('neighborhood', 'models', 'benchmark/pedestrian');
  if (!path) throw new Error('Missing Adventure benchmark pedestrian asset');
  const url = `${import.meta.env.BASE_URL}${path}`;
  let fallback: AdventureActor | undefined = buildFallbackPedestrian(scene, options);
  fallback.root.name = 'adventurePedestrianFallback';
  fallback.root.parent = root;
  let disposed = false, container: AssetContainer | undefined;
  let walk: AnimationGroup | undefined, pose: RestPose[] = [];
  let previousTime: number | undefined, phase = 0, activity = 0;
  options.loadState?.pending.add(url);
  void LoadAssetContainerAsync(url, scene).then(loaded => {
    if (disposed || scene.isDisposed) { loaded.dispose(); return; }
    container = loaded;
    loaded.addAllToScene();
    for (const node of loaded.rootNodes) {
      if (node instanceof TransformNode) node.parent = root;
    }
    for (const mesh of loaded.meshes) {
      mesh.isPickable = false;
      if (options.carryingBag === false && /marketBag/.test(mesh.name)) mesh.setEnabled(false);
      mesh.receiveShadows = true;
      if (mesh instanceof Mesh && mesh.getTotalVertices() > 0) {
        mesh.name = `adventurePedestrian_${mesh.name}`;
        // Animated limbs can leave the neutral-pose bounds. Keep this one
        // near-field character active instead of allowing skinning to pop.
        mesh.alwaysSelectAsActiveMesh = true;
        meshes.push(mesh);
      }
    }
    for (const material of loaded.materials) {
      if (!(material instanceof PBRMaterial)) continue;
      material.environmentIntensity = .75;
      if (/hair|brow|lashes/i.test(material.name)) {
        material.transparencyMode = PBRMaterial.PBRMATERIAL_ALPHATEST;
        material.alphaCutOff = .38;
        material.backFaceCulling = false;
      }
    }
    walk = loaded.animationGroups.find(group => /PedestrianWalk/.test(group.name)) ?? loaded.animationGroups[0];
    if (walk) {
      const targets = new Set<TransformNode>();
      for (const entry of walk.targetedAnimations) {
        if (entry.target instanceof TransformNode) targets.add(entry.target);
      }
      pose = [...targets].map(node => ({
        node, position: node.position.clone(),
        rotation: node.rotationQuaternion?.clone() ?? Quaternion.FromEulerVector(node.rotation),
      }));
      walk.start(true);
      walk.pause();
      // Loading never changes the simulation's clock or pose.
      walk.goToFrame(walk.from);
      for (const rest of pose) {
        rest.node.position.copyFrom(rest.position);
        rest.node.rotationQuaternion = rest.rotation.clone();
      }
    }
    fallback?.dispose();
    fallback = undefined;
    options.loadState?.pending.delete(url);
    options.loadState?.failed.delete(url);
    options.onReady?.(meshes);
  }).catch(error => {
    if (disposed) return;
    options.loadState?.pending.delete(url);
    options.loadState?.failed.add(url);
    console.error('Adventure pedestrian failed to load', error);
  });
  return {
    root, meshes,
    update(timeS, speedMps) {
      const dt = previousTime === undefined ? 0 : Math.max(0, Math.min(.1, timeS - previousTime));
      previousTime = timeS;
      fallback?.update(timeS, speedMps);
      // One authored gait cycle covers 1.2m. This follows distance travelled,
      // with a smooth neutral blend while the courtesy guard stops the walker.
      phase = (phase + dt * Math.abs(speedMps) / 1.2) % 1;
      activity += (Math.min(1, Math.abs(speedMps) / .85) - activity) * (1 - Math.exp(-dt * 10));
      if (!walk || disposed) return;
      walk.goToFrame(walk.from + phase * (walk.to - walk.from));
      for (const rest of pose) {
        Vector3.LerpToRef(rest.position, rest.node.position, activity, rest.node.position);
        if (rest.node.rotationQuaternion) {
          Quaternion.SlerpToRef(rest.rotation, rest.node.rotationQuaternion, activity, rest.node.rotationQuaternion);
        }
      }
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      options.loadState?.pending.delete(url);
      fallback?.dispose();
      fallback = undefined;
      container?.dispose();
      root.dispose();
      meshes.length = 0;
      pose = [];
    },
  };
}
